// client/src/components/media/MediaTagOverlay.jsx
// Click-to-tag overlay for images. Users click a spot on the image, type a
// @handle, and the tag is stored as { username, x, y } where x/y are 0-1
// fractions.  Designed for Instagram/Facebook user_tags but usable anywhere.

import React, { useState, useRef, useCallback } from 'react';

const STORAGE_KEY = 'onscreen_recent_handles';

function getRecentHandles() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
  catch { return []; }
}

// ---------------------------------------------------------------------------
// Single tag badge rendered on the image
// ---------------------------------------------------------------------------
function TagBadge({ tag, onRemove }) {
  return (
    <div
      className="mt-badge"
      style={{ left: `${tag.x * 100}%`, top: `${tag.y * 100}%` }}
    >
      <span className="mt-badge-label">@{tag.username}</span>
      {onRemove && (
        <button className="mt-badge-remove" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
          &times;
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline handle picker shown at the click point
// ---------------------------------------------------------------------------
function HandlePicker({ position, onSelect, onCancel }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const recent = getRecentHandles();
  const filtered = query.length === 0
    ? recent.slice(0, 6)
    : recent.filter(h => h.toLowerCase().startsWith(query.toLowerCase())).slice(0, 6);

  const commit = (handle) => {
    const clean = handle.replace(/^@/, '').trim();
    if (clean) onSelect(clean);
  };

  return (
    <div
      className="mt-picker"
      style={{ left: `${position.x * 100}%`, top: `${position.y * 100}%` }}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        autoFocus
        className="mt-picker-input"
        placeholder="@username"
        value={query}
        onChange={(e) => setQuery(e.target.value.replace(/^@/, ''))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(query); }
          if (e.key === 'Escape') onCancel();
        }}
      />
      {filtered.length > 0 && (
        <div className="mt-picker-list">
          {filtered.map(h => (
            <div key={h} className="mt-picker-item" onMouseDown={(e) => { e.preventDefault(); commit(h); }}>
              @{h}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main overlay component
// ---------------------------------------------------------------------------
export default function MediaTagOverlay({
  src,
  tags = [],
  onChange,
  maxTags = 20,
  disabled = false,
  style,
  onError,
  objectFit = 'cover',
}) {
  const containerRef = useRef(null);
  const [picker, setPicker] = useState(null); // { x, y } fractional

  const handleImageClick = useCallback((e) => {
    if (disabled || !onChange) return;
    if (tags.length >= maxTags) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    setPicker({ x, y });
  }, [disabled, onChange, tags.length, maxTags]);

  const handleSelect = useCallback((username) => {
    if (!picker) return;
    const newTag = { username, x: picker.x, y: picker.y };
    onChange([...tags, newTag]);
    setPicker(null);
  }, [picker, tags, onChange]);

  const handleRemove = useCallback((idx) => {
    onChange(tags.filter((_, i) => i !== idx));
  }, [tags, onChange]);

  return (
    <div ref={containerRef} className="mt-container" style={style} onClick={handleImageClick}>
      <img src={src} alt="" className="mt-image" draggable={false} onError={onError} style={{ objectFit }} />

      {/* Existing tags */}
      {tags.map((tag, i) => (
        <TagBadge key={`${tag.username}-${i}`} tag={tag} onRemove={onChange ? () => handleRemove(i) : null} />
      ))}

      {/* Handle picker */}
      {picker && (
        <HandlePicker
          position={picker}
          onSelect={handleSelect}
          onCancel={() => setPicker(null)}
        />
      )}

      {/* Hint when taggable */}
      {!disabled && onChange && tags.length === 0 && !picker && (
        <div className="mt-hint">Click to tag people</div>
      )}

      <style>{`
        .mt-container {
          position: relative;
          display: inline-block;
          cursor: crosshair;
          user-select: none;
          border-radius: 8px;
          overflow: hidden;
        }
        .mt-image {
          display: block;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        /* Tag badge */
        .mt-badge {
          position: absolute;
          transform: translate(-50%, -100%);
          display: flex;
          align-items: center;
          gap: 2px;
          background: rgba(0, 0, 0, 0.75);
          color: #fff;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
          pointer-events: auto;
          animation: mt-pop 0.15s ease-out;
          z-index: 2;
        }
        .mt-badge::after {
          content: '';
          position: absolute;
          bottom: -5px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 5px solid rgba(0, 0, 0, 0.75);
        }
        @keyframes mt-pop {
          from { opacity: 0; transform: translate(-50%, -100%) scale(0.9); }
          to   { opacity: 1; transform: translate(-50%, -100%) scale(1); }
        }
        .mt-badge-label {
          color: #1d9bf0;
        }
        .mt-badge-remove {
          background: none;
          border: none;
          color: #aaa;
          font-size: 14px;
          line-height: 1;
          cursor: pointer;
          padding: 0 2px;
          margin-left: 2px;
        }
        .mt-badge-remove:hover { color: #ff4444; }

        /* Handle picker */
        .mt-picker {
          position: absolute;
          transform: translate(-50%, 8px);
          z-index: 10;
          background: #fff;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          box-shadow: 0 6px 20px rgba(0,0,0,0.2);
          width: 180px;
          overflow: hidden;
          animation: mt-pop 0.12s ease-out;
        }
        .mt-picker-input {
          width: 100%;
          padding: 8px 10px;
          border: none;
          border-bottom: 1px solid #f0f0f0;
          font-size: 13px;
          font-weight: 500;
          outline: none;
        }
        .mt-picker-input::placeholder { color: #aaa; }
        .mt-picker-list {
          max-height: 160px;
          overflow-y: auto;
        }
        .mt-picker-item {
          padding: 7px 10px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          color: #333;
          transition: background 0.1s;
        }
        .mt-picker-item:hover {
          background: #e8f0fe;
          color: #1d9bf0;
        }

        /* Hint */
        .mt-hint {
          position: absolute;
          bottom: 8px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0,0,0,0.6);
          color: #fff;
          font-size: 11px;
          padding: 4px 10px;
          border-radius: 12px;
          pointer-events: none;
          opacity: 0.8;
        }
      `}</style>
    </div>
  );
}
